/* ═══════════════════════════════════════════════════════════════
   BEAUTYONTAPP — iOS GLASS BOTTOM SHEET ENGINE
   Spring physics + touch gestures for shopModal1/2/3
   ~4 KB · Zero dependencies · Vanilla JS
   ═══════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // ─── SPRING PHYSICS CONFIG (matches Apple's sheet preset) ───
  var SPRING = { stiffness: 300, damping: 30, mass: 1 };
  var VELOCITY_THRESHOLD = 0.5;   // px/ms — fast swipe threshold
  var RUBBER_BAND_COEFF = 0.55;   // Apple's rubber-band constant
  var DISMISS_DISTANCE = 0.35;    // 35% of sheet height = dismiss
  var MAX_OVERSCROLL = 40;        // max px the sheet can pull up past open

  // ─── ANIMATION CANCELLATION ───
  // Each sheet tracks its own animation ID. Starting a new animation
  // increments the ID, causing any running animation to stop on its
  // next frame. Prevents double-tap race conditions.
  var sheetAnimIds = {};

  function getAnimId(sheet) {
    var id = sheet.id || 'sheet';
    if (!sheetAnimIds[id]) sheetAnimIds[id] = 0;
    return id;
  }

  function nextAnimId(sheet) {
    var id = getAnimId(sheet);
    sheetAnimIds[id] = sheetAnimIds[id] + 1;
    return sheetAnimIds[id];
  }

  function isAnimCurrent(sheet, animId) {
    var id = getAnimId(sheet);
    return sheetAnimIds[id] === animId;
  }

  // ─── SPRING ANIMATION SOLVER ───
  function springAnimate(sheet, opts) {
    var pos = opts.from;
    var vel = opts.velocity || 0;
    var to = opts.to;
    var onUpdate = opts.onUpdate;
    var onComplete = opts.onComplete;
    var lastTime = performance.now();
    var animId = nextAnimId(sheet);

    function step(now) {
      // Stop if a newer animation has started
      if (!isAnimCurrent(sheet, animId)) return;

      var frameDt = Math.min((now - lastTime) / 1000, 0.064);
      lastTime = now;
      // Integrate in fixed <=16ms substeps: semi-implicit Euler with k=300, c=30
      // diverges above dt ~0.052s, so never take one big step on slow frames.
      while (frameDt > 0) {
        var dt = Math.min(frameDt, 0.016);
        frameDt -= dt;
        var displacement = pos - to;
        var accel = (-SPRING.stiffness * displacement - SPRING.damping * vel) / SPRING.mass;
        vel += accel * dt;
        pos += vel * dt;
      }
      onUpdate(pos);

      if (Math.abs(vel) < 0.5 && Math.abs(pos - to) < 0.5) {
        onUpdate(to);
        if (onComplete) onComplete();
      } else {
        requestAnimationFrame(step);
      }
    }
    requestAnimationFrame(step);
  }

  // ─── RUBBER BAND FORMULA ───
  function rubberBand(offset, dimension) {
    return (1.0 - 1.0 / ((offset * RUBBER_BAND_COEFF / dimension) + 1.0)) * dimension;
  }

  // ─── SET SHEET POSITION ───
  // Allows negative values down to -MAX_OVERSCROLL for rubber band effect
  function setSheetY(sheet, y) {
    var clamped = Math.max(-MAX_OVERSCROLL, y);
    sheet.style.transform = 'translateY(' + clamped + 'px)';
  }

  // ─── GET CURRENT Y FROM TRANSFORM ───
  function getCurrentY(sheet) {
    var transform = sheet.style.transform;
    var match = transform.match(/translateY\(([^)]+)px\)/);
    return match ? parseFloat(match[1]) : 0;
  }

  // ─── BACKDROP ───
  var backdrop = null;
  function getBackdrop() {
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'sheet-backdrop';
      document.body.appendChild(backdrop);
      backdrop.addEventListener('click', function () {
        closeAllSheets();
      });
    }
    return backdrop;
  }

  function showBackdrop() {
    var bd = getBackdrop();
    bd.offsetHeight; // force reflow
    bd.classList.add('visible');
  }

  function hideBackdrop() {
    var bd = getBackdrop();
    bd.classList.remove('visible');
  }

  // ─── ACTIVE SHEET TRACKING ───
  var activeSheet = null;

  function closeAllSheets() {
    var sheets = document.querySelectorAll('.shop-modal.glass-sheet.sheet-open');
    for (var i = 0; i < sheets.length; i++) {
      animateClose(sheets[i]);
    }
  }

  // ─── INJECT DRAG HANDLE INTO A MODAL ───
  function injectDragHandle(modal) {
    if (modal.querySelector('.sheet-drag-handle')) return;
    var handle = document.createElement('div');
    handle.className = 'sheet-drag-handle';
    handle.innerHTML = '<div class="sheet-drag-pill"></div>';
    modal.insertBefore(handle, modal.firstChild);
    return handle;
  }

  // ─── ANIMATE OPEN ───
  function animateOpen(sheet) {
    activeSheet = sheet;
    sheet.classList.add('glass-sheet', 'glass-enabled', 'sheet-animating');
    sheet.style.display = 'block';
    sheet.style.visibility = 'visible';
    sheet.style.opacity = '1';
    sheet.classList.remove('closing');
    sheet.classList.add('open');

    showBackdrop();
    document.body.classList.add('sheet-scroll-lock');

    var sheetHeight = sheet.offsetHeight;
    setSheetY(sheet, sheetHeight);

    requestAnimationFrame(function () {
      springAnimate(sheet, {
        from: sheetHeight,
        to: 0,
        velocity: 0,
        onUpdate: function (y) { setSheetY(sheet, y); },
        onComplete: function () {
          sheet.classList.add('sheet-open');
          sheet.classList.remove('sheet-animating');
          sheet.style.willChange = 'auto';
        }
      });
    });
  }

  // ─── ANIMATE CLOSE ───
  function animateClose(sheet, velocity) {
    sheet.classList.add('sheet-animating');
    sheet.style.willChange = 'transform';
    var sheetHeight = sheet.offsetHeight;

    springAnimate(sheet, {
      from: getCurrentY(sheet),
      to: sheetHeight + 20,
      velocity: velocity || 0,
      onUpdate: function (y) { setSheetY(sheet, y); },
      onComplete: function () {
        sheet.classList.remove('sheet-open', 'open', 'glass-sheet', 'glass-enabled', 'sheet-animating');
        sheet.style.transform = '';
        sheet.style.willChange = 'auto';
        sheet.style.display = '';
        sheet.style.visibility = '';
        // Only the sheet that is still active may clear global state: a sheet
        // finishing its close AFTER a replacement opened must not hide the
        // backdrop or null the new activeSheet (sheet-switch race).
        if (activeSheet === sheet) {
          activeSheet = null;
          hideBackdrop();
        }
        if (!document.querySelector('.shop-modal.open')) {
          document.body.classList.remove('sheet-scroll-lock');
        }
      }
    });
  }

  // ─── TOUCH GESTURE HANDLER FOR DRAG HANDLE ───
  function initGesture(sheet) {
    var handle = sheet.querySelector('.sheet-drag-handle');
    if (!handle) return;

    var startY = 0, lastY = 0, lastTime = 0, velocityY = 0;
    var sheetHeight = 0, isDragging = false;

    handle.addEventListener('pointerdown', function (e) {
      if (!sheet.classList.contains('sheet-open')) return;
      e.preventDefault();
      handle.setPointerCapture(e.pointerId);
      isDragging = true;
      startY = e.clientY;
      lastY = e.clientY;
      lastTime = performance.now();
      velocityY = 0;
      sheetHeight = sheet.offsetHeight;
      // Cancel any running animation
      nextAnimId(sheet);
      sheet.style.transition = 'none';
      sheet.style.willChange = 'transform';
      sheet.classList.add('sheet-animating');
    });

    handle.addEventListener('pointermove', function (e) {
      if (!isDragging) return;
      var now = performance.now();
      var dt = now - lastTime;
      if (dt > 0) velocityY = (e.clientY - lastY) / dt;
      lastY = e.clientY;
      lastTime = now;
      var deltaY = e.clientY - startY;
      if (deltaY < 0) deltaY = -rubberBand(-deltaY, sheetHeight);
      setSheetY(sheet, deltaY);
    });

    handle.addEventListener('pointerup', function (e) {
      if (!isDragging) return;
      isDragging = false;
      handle.releasePointerCapture(e.pointerId);
      sheet.style.willChange = 'transform';
      // Stillness guard: pointermove only fires on movement, so after a
      // drag-then-pause the last velocity sample is stale. Zero it so the
      // 35% distance rule decides; real flicks (<16ms-old sample) unaffected.
      if (performance.now() - lastTime > 100) velocityY = 0;

      var currentY = getCurrentY(sheet);
      var shouldDismiss = false;

      if (velocityY > VELOCITY_THRESHOLD) {
        shouldDismiss = true;
      } else if (currentY > sheetHeight * DISMISS_DISTANCE) {
        shouldDismiss = true;
      } else if (velocityY < -VELOCITY_THRESHOLD) {
        shouldDismiss = false;
      }

      if (shouldDismiss) {
        animateClose(sheet, velocityY * 1000);
      } else {
        springAnimate(sheet, {
          from: currentY, to: 0, velocity: velocityY * 1000,
          onUpdate: function (y) { setSheetY(sheet, y); },
          onComplete: function () {
            sheet.classList.remove('sheet-animating');
            sheet.style.willChange = 'auto';
          }
        });
      }
    });

    handle.addEventListener('pointercancel', function () {
      if (!isDragging) return;
      isDragging = false;
      springAnimate(sheet, {
        from: getCurrentY(sheet), to: 0, velocity: 0,
        onUpdate: function (y) { setSheetY(sheet, y); },
        onComplete: function () {
          sheet.classList.remove('sheet-animating');
          sheet.style.willChange = 'auto';
        }
      });
    });
  }

  // ─── SWIPE-TO-DISMISS ON SHEET BODY ───
  function initBodySwipe(sheet) {
    var startY = 0, lastY = 0, lastTime = 0, velocityY = 0;
    var isDragging = false, sheetHeight = 0;

    sheet.addEventListener('touchstart', function (e) {
      if (!sheet.classList.contains('sheet-open')) return;
      if (sheet.scrollTop > 5) return;
      startY = e.touches[0].clientY;
      lastY = startY;
      lastTime = performance.now();
      velocityY = 0;
      sheetHeight = sheet.offsetHeight;
      isDragging = false;
    }, { passive: true });

    sheet.addEventListener('touchmove', function (e) {
      if (!sheet.classList.contains('sheet-open')) return;
      if (sheet.scrollTop > 5) return;
      var currentTouch = e.touches[0].clientY;
      var deltaY = currentTouch - startY;
      if (deltaY < 10 && !isDragging) return;
      isDragging = true;
      // Cancel any running animation on first drag frame
      if (deltaY >= 10) nextAnimId(sheet);
      var now = performance.now();
      var dt = now - lastTime;
      if (dt > 0) velocityY = (currentTouch - lastY) / dt;
      lastY = currentTouch;
      lastTime = now;
      e.preventDefault();
      setSheetY(sheet, deltaY);
    }, { passive: false });

    sheet.addEventListener('touchend', function () {
      if (!isDragging) return;
      isDragging = false;
      // Stillness guard (same as drag handle): stale velocity after a pause
      if (performance.now() - lastTime > 100) velocityY = 0;
      var currentY = getCurrentY(sheet);
      var shouldDismiss = velocityY > VELOCITY_THRESHOLD || currentY > sheetHeight * DISMISS_DISTANCE;

      if (shouldDismiss) {
        animateClose(sheet, velocityY * 1000);
      } else {
        springAnimate(sheet, {
          from: currentY, to: 0, velocity: velocityY * 1000,
          onUpdate: function (y) { setSheetY(sheet, y); },
          onComplete: function () {
            sheet.classList.remove('sheet-animating');
            sheet.style.willChange = 'auto';
          }
        });
      }
    }, { passive: true });
  }

  // ─── INIT ───
  function init() {
    var modals = [
      document.getElementById('shopModal1'),
      document.getElementById('shopModal2'),
      document.getElementById('shopModal3')
    ];

    modals.forEach(function (modal) {
      if (!modal) return;
      injectDragHandle(modal);
      initGesture(modal);
      initBodySwipe(modal);
    });

    // Override existing open buttons
    var shopBtns = [
      document.getElementById('shopBtn1'),
      document.getElementById('shopBtn2'),
      document.getElementById('shopBtn3')
    ];

    // Shared pending-switch timer: rapid taps on two different dock buttons
    // <350ms apart must cancel the queued open, or two sheets fight over
    // activeSheet/backdrop state.
    var pendingOpen = null;

    shopBtns.forEach(function (btn, index) {
      if (!btn || !modals[index]) return;
      var newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);

      newBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (pendingOpen) { clearTimeout(pendingOpen); pendingOpen = null; }

        if (activeSheet && activeSheet !== modals[index]) {
          animateClose(activeSheet);
          pendingOpen = setTimeout(function () {
            pendingOpen = null;
            animateOpen(modals[index]);
          }, 350);
        } else if (activeSheet === modals[index]) {
          animateClose(modals[index]);
        } else {
          animateOpen(modals[index]);
        }
      });
    });

    // Override existing close buttons
    var closeBtns = document.querySelectorAll('.shop-modal .close-btn');
    closeBtns.forEach(function (btn) {
      var newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      newBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var modal = newBtn.closest('.shop-modal');
        if (modal) animateClose(modal);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 100);
  }

})();
